// components/ImageTextEditor.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Stage, Layer, Image, Text, Rect, Group, Arrow } from "react-konva"
import useImage from "use-image"
import CodePreviewSwitch from "../components/ImageTextEditor/CodePreviewSwitch"
import variableList from "../components/variables"
import VariablesInputs from "../components/VariablesInputs/Index"
import MyBreadcrumb from "../components/ImageTextEditor/MyBreadcrumb"
import FloatingVariableList from "../components/ImageTextEditor/FloatingVariableList"
import bannersStore from "../stores/bannersStore"
import EditButton from "../components/ImageTextEditor/EditButton"
import CreateButton from "../components/ImageTextEditor/CreateButton"
import { Switch } from "antd"
import RanksystemInputs from "../components/VariablesInputs/RanksystemInputs"

// Fixed Background Image Component
const BackgroundImage = ({ imageUrl }) => {
    const [image] = useImage(imageUrl)

    return <Image image={image} width={960} height={360} />
}

const ImageTextEditor = () => {
    const stageRef = useRef()
    const [backgroundImage, setBackgroundImage] = useState(null)
    const [imageFile, setImageFile] = useState(null)
    const [texts, setTexts] = useState([])
    const [selectedText, setSelectedText] = useState(null)
    const [selectedTextObj, setSelectedTextObj] = useState(null)
    const [isPreview, setIsPreview] = useState(false)
    const selectedBanner = bannersStore((state) => state.selectedBanner)
    const [fileInputKey, setFileInputKey] = useState(Date.now())
    const [useRanksystem, setUseRanksystem] = useState(false)
    const [ranksystemConfig, setRanksystemConfig] = useState({})

    // Text styling options
    const [fontSize, setFontSize] = useState(36)
    const [fontFamily, setFontFamily] = useState("Arial")
    const [textColor, setTextColor] = useState("#ffffff")
    const [fontWeight, setFontWeight] = useState("normal")
    const [rotation, setRotation] = useState(0) // New rotation state

    // Add this helper function to calculate text dimensions
    const calculateTextDimensions = (text, fontSize, fontFamily) => {
        const canvas = document.createElement("canvas")
        const context = canvas.getContext("2d")
        context.font = `${fontSize}px ${fontFamily}`
        const metrics = context.measureText(text)
        return {
            width: metrics.width,
            height: fontSize * 1.2, // Approximate height
        }
    }

    const variables = useMemo(() => variableList, [])

    // Handle background image upload
    const handleImageUpload = (event) => {
        const file = event.target.files[0]
        if (!file) return
        setImageFile(file)

        const reader = new FileReader()
        reader.onload = (e) => {
            setBackgroundImage(e.target.result)
        }
        reader.readAsDataURL(file)
    }

    // Add new text to the image - useCallback to memoize
    const addText = useCallback(
        (variable) => {
            if (!backgroundImage) return
            const newText = {
                id: Date.now(),
                variable: variable,
                text: isPreview ? variable.value : `{${variable.key}}`, // Store the original text with variables
                displayText: isPreview ? variable.value : `{${variable.key}}`, // Store the text with variables replaced
                x: 50,
                y: 50,
                fontSize: fontSize,
                fontFamily: fontFamily,
                fill: textColor,
                fontWeight: fontWeight,
                rotation: rotation,
            }

            setTexts((prevTexts) => [...prevTexts, newText])
            setSelectedText(newText.id)
        },
        [fontSize, fontFamily, textColor, fontWeight, rotation, isPreview, backgroundImage]
    )

    // Update selected text properties - useCallback to memoize
    const updateSelectedText = useCallback(
        (property, value) => {
            if (!selectedText) return

            setSelectedTextObj((oldValue) => ({ ...oldValue, [property]: value }))

            setTexts((prevTexts) =>
                prevTexts.map((text) => {
                    if (text.id === selectedText) {
                        const updatedText = { ...text, [property]: value }

                        return updatedText
                    }
                    return text
                })
            )
        },
        [selectedText]
    )

    // Handle text selection - useCallback to memoize
    const handleTextClick = useCallback((textId) => {
        setSelectedText(textId)
    }, [])

    // Delete selected text - useCallback to memoize
    const deleteSelectedText = useCallback(() => {
        if (!selectedText) return
        setTexts((prevTexts) => prevTexts.filter((text) => text.id !== selectedText))
        setSelectedText(null)
    }, [selectedText])

    const deleteText = useCallback((textId) => {
        setTexts((prevTexts) => prevTexts.filter((text) => text.id !== textId))
    }, [])

    // Get selected text object for styling controls
    useEffect(() => {
        setSelectedTextObj(texts.find((t) => t.id == selectedText))
    }, [selectedText])

    useEffect(() => {
        if (isPreview) {
            setTexts((prevTexts) =>
                prevTexts.map((text) => {
                    return {
                        ...text,
                        displayText:
                            text.options?.numericType == "fa"
                                ? text.variable.faValue
                                : text.variable.value,
                    }
                })
            )
        } else {
            setTexts((prevTexts) =>
                prevTexts.map((text) => ({
                    ...text,
                    displayText: `{${text.variable.key}}`,
                }))
            )
        }
    }, [isPreview])

    useEffect(() => {
        if (selectedBanner) {
            setUseRanksystem(selectedBanner.useRanksystem)

            setTexts(
                selectedBanner.variables.map((v) => {
                    const variableInMemo = variables.find((vInMemo) => vInMemo.key == v.key)

                    return {
                        id: v.id,
                        variable: variableInMemo,
                        text: isPreview ? variableInMemo.value : `{${variableInMemo.key}}`, // Store the original text with variables
                        displayText: isPreview ? variableInMemo.value : `{${variableInMemo.key}}`, // Store the text with variables replaced
                        x: v.x,
                        y: v.y,
                        fontSize: v.fontSize,
                        fontFamily: v.fontFamily,
                        fill: v.fill,
                        fontWeight: v.fontWeight,
                        rotation: v.rotation,
                        options: v.options,
                    }
                })
            )
            setBackgroundImage(selectedBanner.url || null)
            setRanksystemConfig(selectedBanner.ranksystemConfig)
        }
    }, [selectedBanner])

    return (
        <>
            <header
                style={{
                    textAlign: "center",
                    background: "#1e1e1e",
                    borderBottom: "1px solid #333",
                    padding: "20px",
                    color: "#e0e0e0",
                }}
            >
                <h1
                    style={{
                        margin: 0,
                        color: "#ffffff",
                        fontSize: "2rem",
                        fontWeight: "600",
                    }}
                >
                    بنر ادیتور
                </h1>
                <p
                    style={{
                        margin: "10px 0 0 0",
                        color: "#b0b0b0",
                        fontSize: "1.1rem",
                    }}
                >
                    برای کانفیگ لطفا عکس بک گراند خود را اپلود کنید
                </p>
            </header>
            <FloatingVariableList
                variables={texts}
                setSelectedText={setSelectedText}
                deleteText={deleteText}
            />
            <CodePreviewSwitch setIsPreview={setIsPreview} isPreview={isPreview} />
            <MyBreadcrumb />
            <div className="image-text-editor h-full w-full">
                {/* Canvas Area */}
                <div className="canvas-area">
                    {!backgroundImage ? (
                        <div className="canvas-placeholder">
                            <p>برای شروع لطفا عکس بک گراند خود ر ا اپلود کنید</p>
                        </div>
                    ) : (
                        <Stage width={958} height={360} ref={stageRef}>
                            <Layer>
                                {backgroundImage && <BackgroundImage imageUrl={backgroundImage} />}
                                {texts.map((text) => (
                                    <Group
                                        key={text.id}
                                        onClick={() => handleTextClick(text.id)}
                                        onTap={() => handleTextClick(text.id)}
                                        onDragStart={() => handleTextClick(text.id)}
                                        x={text.x}
                                        y={text.y}
                                        rotation={text.rotation || 0}
                                        draggable={true}
                                        onDragEnd={(e) => {
                                            const node = e.target
                                            setTexts((prevTexts) =>
                                                prevTexts.map((t) =>
                                                    t.id === text.id
                                                        ? {
                                                              ...t,
                                                              x: node.x(),
                                                              y: node.y(),
                                                          }
                                                        : t
                                                )
                                            )
                                        }}
                                        onTransformEnd={(e) => {
                                            const node = e.target
                                            setTexts((prevTexts) =>
                                                prevTexts.map((t) =>
                                                    t.id === text.id
                                                        ? {
                                                              ...t,
                                                              x: node.x(),
                                                              y: node.y(),
                                                              rotation: node.rotation(),
                                                          }
                                                        : t
                                                )
                                            )
                                        }}
                                        shadowColor={
                                            selectedText === text.id ? "rgba(0,0,255,0.5)" : null
                                        }
                                        shadowBlur={selectedText === text.id ? 10 : 0}
                                        shadowOffset={
                                            selectedText === text.id ? { x: 0, y: 0 } : null
                                        }
                                    >
                                        {selectedText === text.id && (
                                            <Rect
                                                x={-5}
                                                y={-5}
                                                width={
                                                    calculateTextDimensions(
                                                        text.displayText,
                                                        text.fontSize,
                                                        text.fontFamily
                                                    ).width + 10
                                                } // You'll need to calculate text width
                                                height={
                                                    calculateTextDimensions(
                                                        text.displayText,
                                                        text.fontSize,
                                                        text.fontFamily
                                                    ).height + 10
                                                } // You'll need to calculate text height
                                                stroke="#1890ff"
                                                strokeWidth={2}
                                                fill="transparent"
                                                dash={[5, 5]}
                                            />
                                        )}

                                        <Text
                                            x={0} // Relative to group
                                            y={0} // Relative to group
                                            text={text.displayText}
                                            fontSize={text.fontSize}
                                            fontFamily={text.fontFamily}
                                            fill={text.fill}
                                            fontStyle={` ${text.fontWeight}`}
                                        />
                                    </Group>
                                ))}
                            </Layer>
                        </Stage>
                    )}
                </div>
                {/* Controls Panel */}
                <div className="controls-panel">
                    <div className="control-section">
                        <h3>عکس بک گراند</h3>
                        <input
                            key={fileInputKey}
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="file-input"
                        />
                        {backgroundImage && (
                            <button
                                onClick={() => {
                                    setBackgroundImage(null)
                                    setTexts([])
                                    // Reset the file input by changing its key
                                    setFileInputKey(Date.now())
                                }}
                                className="btn btn-danger"
                                style={{ marginTop: "10px" }}
                            >
                                حذف بک گراند
                            </button>
                        )}
                    </div>
                    <VariablesInputs
                        updateSelectedText={updateSelectedText}
                        selectedTextObj={selectedTextObj}
                    />

                    <div className="control-section">
                        <h3>متغیرها</h3>
                        <p className="variable-help">برای اضافه کردن متغیر کلیک کنید</p>
                        <div className="variables-grid">
                            {variables
                                .filter((v) => !v.key.startsWith("rs-"))
                                .map((variable, index) => (
                                    <button
                                        key={index}
                                        onClick={() => addText(variable)}
                                        className="variable-btn"
                                        title={`Value: ${variable.value}`}
                                    >
                                        <div className="variable-key">{variable.key}</div>
                                        <div className="variable-value">
                                            {variable.description}
                                        </div>
                                    </button>
                                ))}
                        </div>
                    </div>

                    <div className="control-section">
                        <div className="text-blue-600 flex justify-between">
                            رنک سیستم
                            <Switch
                                value={useRanksystem}
                                onChange={(checked) => setUseRanksystem(checked)}
                            />
                        </div>
                        {useRanksystem ? (
                            <>
                                <RanksystemInputs
                                    ranksystemConfig={ranksystemConfig}
                                    setRanksystemConfig={setRanksystemConfig}
                                />
                                <div>
                                    <h3>متغیرهای رنک سیستم</h3>
                                    <div className="variables-grid">
                                        {variables
                                            .filter((v) => v.key.startsWith("rs-"))
                                            .map((variable, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => addText(variable)}
                                                    className="variable-btn"
                                                    title={`Value: ${variable.value}`}
                                                >
                                                    <div className="variable-key">
                                                        {variable.key}
                                                    </div>
                                                    <div className="variable-value">
                                                        {variable.description}
                                                    </div>
                                                </button>
                                            ))}
                                    </div>
                                </div>
                            </>
                        ) : null}
                    </div>
                    {selectedTextObj && (
                        <div className="control-section" dir="rtl">
                            <h3>ویرایش متن انتخاب شده</h3>

                            <div className="style-control">
                                <label>سایز فونت:</label>
                                <input
                                    type="range"
                                    min="12"
                                    max="72"
                                    value={selectedTextObj.fontSize || fontSize}
                                    onChange={(e) => {
                                        const size = parseInt(e.target.value)
                                        updateSelectedText("fontSize", size)
                                    }}
                                />
                                <span>{selectedTextObj.fontSize || fontSize}px</span>
                            </div>

                            <div className="style-control">
                                <label>انتخاب فونت:</label>
                                <select
                                    value={selectedTextObj.fontFamily || fontFamily}
                                    onChange={(e) => {
                                        updateSelectedText("fontFamily", e.target.value)
                                    }}
                                >
                                    <option value="Exo2">Exo2</option>
                                    <option value="Candu">Candu</option>
                                    <option value="Montserrat">Montserrat</option>
                                    <option value="Segoeui">Segoeui</option>
                                    <option value="Arial">Arial</option>
                                    <option value="SourceCodePro">Source Code Pro</option>
                                    <option value="RubikGemstones">Rubik Gemstones</option>
                                    <option value="MountainsofChristmas">
                                        MountainsofChristmas
                                    </option>
                                    <option value="Courier New">Courier New</option>
                                    <option value="BBHBartle">BBHBartle</option>
                                </select>
                            </div>

                            <div className="style-control">
                                <label>وزن فونت:</label>
                                <select
                                    value={selectedTextObj.fontWeight || fontWeight}
                                    onChange={(e) => {
                                        updateSelectedText("fontWeight", e.target.value)
                                    }}
                                >
                                    <option value="normal">Normal</option>
                                    <option value="bold">Bold</option>
                                    <option value="300">Light</option>
                                    <option value="600">Semi-Bold</option>
                                </select>
                            </div>

                            <div className="style-control">
                                <label>رنگ:</label>
                                <input
                                    type="color"
                                    value={selectedTextObj.fill || textColor}
                                    onChange={(e) => {
                                        updateSelectedText("fill", e.target.value)
                                    }}
                                />
                            </div>

                            {/* Rotation control for selected text */}
                            <div className="style-control">
                                <label>چرخش:</label>
                                <input
                                    type="range"
                                    min="0"
                                    max="360"
                                    value={selectedTextObj.rotation || 0}
                                    onChange={(e) => {
                                        const newRotation = parseInt(e.target.value)
                                        updateSelectedText("rotation", newRotation)
                                    }}
                                />
                                <span>{selectedTextObj.rotation || 0}°</span>
                            </div>

                            <button onClick={deleteSelectedText} className="btn btn-danger">
                                حذف متغیر
                            </button>
                        </div>
                    )}

                    <div className="control-section">
                        {selectedBanner ? (
                            <EditButton
                                texts={texts}
                                imageFile={imageFile}
                                banner={selectedBanner}
                                useRanksystem={useRanksystem}
                                ranksystemConfig={ranksystemConfig}
                                oldVariables={selectedBanner.variables}
                            />
                        ) : (
                            <CreateButton
                                texts={texts}
                                useRanksystem={useRanksystem}
                                ranksystemConfig={ranksystemConfig}
                                imageFile={imageFile}
                            />
                        )}
                    </div>
                </div>
            </div>
        </>
    )
}

export default ImageTextEditor
